import type { Filter, LiteIntegrationOut } from '@repo/api-codegen';
import { FilterOperator } from '@repo/common/components/Filter/types';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { EntityType } from '@repo/common/enums/entityType';
import { find } from 'lodash-es';
import { teamsGrantedFilter, type IIntegration } from '../../../api';
import type { FilterValue } from '../../Filter';
import { FilterOptionType } from '../../Filter';
import IntegrationLogo from '../../IntegrationLogo';
import type { CommonEntityTreeNode } from '../utils';
import { getEntityIconByType } from '../utils';
import { CATALOG_TREE_INTEGRATION_NODE_CHILD_ENTITY_TYPES } from './constants';

export function catalogFilterQueryParamsFromCatalogTreeNode(
	node: CommonEntityTreeNode
): FilterValue[] {
	if (node.entity.entity_type === EntityType.integration) {
		return [
			{
				value: [node.entity.id],
				operator: FilterOperator.Is,
				filterType: FilterOptionType.INTEGRATION,
			},
		];
	}

	return [
		{
			value: [node.entity.id],
			operator: FilterOperator.Is,
			filterType: FilterOptionType.PARENT_ID,
		},
	];
}

export function catalogFilterFromTreeNode(
	node: CommonEntityTreeNode,
	teamId?: string
): Filter[] {
	const filters: Filter[] = [];

	if (node.entity.entity_type === EntityType.integration) {
		filters.push(
			{
				operator: 'not',
				operands: [
					{
						value: '',
						operator: 'is_set',
						field: 'parent_id',
						operands: [],
					},
				],
			},
			{
				value: node.entity.id,
				operator: 'exact',
				field: 'integration_id',
				operands: [],
			},
			{
				value: CATALOG_TREE_INTEGRATION_NODE_CHILD_ENTITY_TYPES,
				operator: 'in',
				field: 'entity_type',
				operands: [],
			}
		);
	} else {
		filters.push({
			value: node.entity.id,
			operator: 'exact',
			field: 'parent_id',
			operands: [],
		});
	}

	if (teamId) {
		filters.push(teamsGrantedFilter(teamId));
	}

	return filters;
}

export const isIntegrationRootNode = (
	integration: IIntegration | LiteIntegrationOut
) => {
	const type = find(
		integrationList,
		(i) => i.type === integration.type
	)?.entity_type;

	if (!type) {
		return false;
	}
	if (['git'].includes(integration.type)) {
		return false;
	}
	return [
		EntityType.table,
		EntityType.dashboard,
		EntityType.job,
		EntityType.event,
	].includes(type);
};

export const getIcon = (node: CommonEntityTreeNode) => {
	if (node.entity.entity_type === EntityType.integration) {
		const integrationType = node.integration?.type;
		let integrationId: string | undefined;

		if (
			integrationType &&
			['custom', 'marketplace'].includes(integrationType)
		) {
			integrationId = node.entity.id;
		} else if (node.integration) {
			integrationId = undefined;
		} else {
			integrationId = node.entity.id;
		}

		return (
			<IntegrationLogo
				integrationId={integrationId}
				integrationType={integrationType}
				size={16}
			/>
		);
	}

	return getEntityIconByType(node.entity.entity_type);
};

export const getLabel = (node: CommonEntityTreeNode) => {
	if (node.entity.entity_type === EntityType.integration) {
		return node.entity.title_cased ?? node.entity.title ?? 'Untitled';
	}

	return node.entity.title_cased ?? node.entity.title ?? 'Untitled';
};
